import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { createPayer, updatePayer } from 'api/Payer';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import { getInitialValues } from './helpers';

export const usePayerForm = ({
  payer,
  practiceId,
  externalInitialValues,
  enableReinitialize = false,
  onSuccess: externalOnSuccess,
  onGoBack
}) => {
  const navigate = useNavigate();
  const formikRef = useRef(null);

  const isUpdate = !!payer;

  const validationSchema = Yup.object().shape({
    name: Yup.string().min(3).required('Payer name is required.')
  });

  const { mutateAsync: payerHandler, isLoading } = useCustomMutation({
    mutationFn: (params) =>
      isUpdate ? updatePayer(navigate, params) : createPayer(navigate, params),
    successMessage: isUpdate ? 'Payer updated successfully!' : 'Payer requested successfully!',
    invalidateQueryKey: ['payers'],
    onSuccess: () => {
      if (formikRef.current) {
        formikRef.current.resetForm();
      }
      if (externalOnSuccess) {
        externalOnSuccess();
        return;
      }

      onGoBack?.();
    }
  });

  const formik = useFormik({
    initialValues: getInitialValues({ externalInitialValues }),
    validationSchema,
    enableReinitialize,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values) => {
      const payerData = {
        address: {
          address_ln_1: values.address_ln_1,
          address_ln_2: values.address_ln_2,
          city: values.city,
          state: values.state,
          zip: values.zip
        },
        name: values.name,
        phone: values.phone,
        type: values.type,
        virtual_modifiers: values.virtual_modifiers,
        in_network_providers: values.in_network_providers,
        out_of_network_providers: values.out_of_network_providers
      };

      if (isUpdate) {
        return payerHandler({ payer: payerData, practiceId, payerId: payer.id });
      }

      payerHandler({ payer: payerData, practiceId });
    }
  });

  formikRef.current = formik;

  return {
    formik,
    isLoading,
    isUpdate
  };
};
